import React, { useEffect, useState } from 'react';
import {
  Search,
  Loader2,
  Plus,
  FileText,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  MoreVertical,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { DriverInvoiceService, DriverInvoice } from '../services/driverInvoiceService';
import { ApiService } from '../services/api';
import { getCurrentOrganisationId } from '../services/supabaseClient';
import { Driver } from '../types';
import CreateInvoiceModal from '../components/CreateInvoiceModal';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import { PaymentMethodDialog } from '../components/PaymentMethodDialog';

const DriverInvoices: React.FC = () => {
  const { addToast } = useToast();

  // Data State
  const [invoices, setInvoices] = useState<(DriverInvoice & { driver?: Driver })[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [stripeInvoiceCapability, setStripeInvoiceCapability] = useState<{ canCreate: boolean; reason?: string } | null>(null);

  // Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [driverFilter, setDriverFilter] = useState<string>('all');

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<DriverInvoice | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [invoiceToSend, setInvoiceToSend] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get organization ID
      const organisationId = await getCurrentOrganisationId();

      // Load invoices and drivers in parallel
      const [invoicesResult, driversData, stripeCapability] = await Promise.all([
        DriverInvoiceService.getOrgDriverInvoices(organisationId),
        ApiService.getDrivers(),
        DriverInvoiceService.canCreateStripeInvoices(organisationId)
      ]);

      if (invoicesResult.success && invoicesResult.data) {
        // Enrich invoices with driver data
        const enrichedInvoices = invoicesResult.data.map(inv => ({
          ...inv,
          driver: driversData?.find(d => d.id === inv.driver_id)
        }));
        setInvoices(enrichedInvoices);
      }

      setDrivers(driversData || []);
      setStripeInvoiceCapability(stripeCapability);
    } catch (error) {
      console.error('Error loading data:', error);
      addToast('Failed to load invoices', 'error');
      setStripeInvoiceCapability(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async (invoiceId: string) => {
    setInvoiceToSend(invoiceId);
    setPaymentDialogOpen(true);
    setActionMenuOpen(null);
  };

  const handlePaymentMethodSelected = async (method: 'stripe_invoice' | 'checkout' | 'saved' | 'manual') => {
    if (!invoiceToSend) return;

    setPaymentDialogOpen(false);

    const result = await DriverInvoiceService.sendInvoice(invoiceToSend, method);
    if (result.success) {
      addToast('Invoice sent successfully', 'success');
      loadData();
    } else {
      addToast(result.error || 'Failed to send invoice', 'error');
    }

    setInvoiceToSend(null);
  };

  const handleMarkPaid = async (invoiceId: string) => {
    const result = await DriverInvoiceService.markInvoicePaid(invoiceId, 'manual');
    if (result.success) {
      addToast('Invoice marked as paid', 'success');
      loadData();
    } else {
      addToast(result.error || 'Failed to mark invoice as paid', 'error');
    }
    setActionMenuOpen(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
      case 'sent':
      case 'viewed':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
      case 'overdue':
        return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
      case 'disputed':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      case 'draft':
        return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
      case 'cancelled':
        return 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={14} />;
      case 'sent':
      case 'viewed':
        return <Send size={14} />;
      case 'overdue':
        return <AlertCircle size={14} />;
      case 'disputed':
        return <AlertCircle size={14} />;
      case 'draft':
        return <FileText size={14} />;
      default:
        return <Clock size={14} />;
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    // Search filter
    const searchMatch =
      inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.driver?.name?.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    const statusMatch = statusFilter === 'all' || inv.status === statusFilter;

    // Driver filter
    const driverMatch = driverFilter === 'all' || inv.driver_id === driverFilter;

    return searchMatch && statusMatch && driverMatch;
  });

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent' || i.status === 'viewed').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    totalAmount: invoices.reduce((sum, i) => sum + Number(i.total_amount || 0), 0),
    paidAmount: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + Number(i.total_amount || 0), 0),
    outstandingAmount: invoices.filter(i => ['sent', 'viewed', 'overdue'].includes(i.status)).reduce((sum, i) => sum + Number(i.amount_due || 0), 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Driver Invoices</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage invoices for driver charges and admin fees.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={loadData}
            className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <Plus size={16} />
            Create Invoice
          </button>
        </div>
      </div>

      {stripeInvoiceCapability && !stripeInvoiceCapability.canCreate && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">Stripe Connect setup is incomplete</p>
          <p className="mt-1">
            {stripeInvoiceCapability.reason || 'You can still send invoices and collect payments manually while completing Stripe onboarding.'}
          </p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <FileText size={20} className="text-slate-600 dark:text-slate-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.total}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Invoices</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <Clock size={20} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">£{stats.outstandingAmount.toFixed(2)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Outstanding</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
              <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{stats.overdue}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Overdue</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <CheckCircle size={20} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">£{stats.paidAmount.toFixed(2)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Collected</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="disputed">Disputed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={driverFilter}
            onChange={e => setDriverFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          >
            <option value="all">All Drivers</option>
            {drivers.map(driver => (
              <option key={driver.id} value={driver.id}>{driver.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-primary-600" size={32} />
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-12 text-center">
          <FileText size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
            {invoices.length === 0 ? 'No invoices yet' : 'No invoices match your filters'}
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            {invoices.length === 0
              ? 'Create your first driver invoice to start charging drivers for PCNs.'
              : 'Try adjusting your search or filter criteria.'}
          </p>
          {invoices.length === 0 && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
            >
              <Plus size={16} />
              Create Invoice
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-950/40 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Invoice</th>
                  <th className="text-left px-4 py-3 font-semibold">Driver</th>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Due Date</th>
                  <th className="text-right px-4 py-3 font-semibold">Amount</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(invoice => {
                  const platformFeePercentage = Number(invoice.platform_fee_percentage || 0);
                  const platformFeeAmount = Number(
                    invoice.platform_fee_amount ??
                    (platformFeePercentage > 0 ? Number(invoice.total_amount || 0) * (platformFeePercentage / 100) : 0)
                  );

                  return (
                  <tr
                    key={invoice.id}
                    className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="font-mono font-bold text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {invoice.invoice_number}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-sm font-bold text-slate-500 dark:text-slate-400">
                          {invoice.driver?.name?.charAt(0) || '?'}
                        </div>
                        <span className="text-slate-800 dark:text-white font-medium">
                          {invoice.driver?.name || 'Unknown Driver'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                      {new Date(invoice.invoice_date).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                      {new Date(invoice.due_date).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-slate-800 dark:text-white">
                        £{Number(invoice.total_amount || 0).toFixed(2)}
                      </span>
                      {platformFeeAmount > 0 && (
                        <span className="block text-xs text-amber-600 dark:text-amber-400">
                          Fee ({platformFeePercentage.toFixed(2)}%): £{platformFeeAmount.toFixed(2)}
                        </span>
                      )}
                      {invoice.amount_due > 0 && invoice.status !== 'paid' && (
                        <span className="block text-xs text-slate-500 dark:text-slate-400">
                          £{Number(invoice.amount_due).toFixed(2)} due
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={() => setActionMenuOpen(actionMenuOpen === invoice.id ? null : invoice.id)}
                          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        >
                          <MoreVertical size={16} />
                        </button>
                        {actionMenuOpen === invoice.id && (
                          <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-10">
                            <button
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                            >
                              <Eye size={14} />
                              View Details
                            </button>
                            {invoice.status === 'draft' && (
                              <button
                                onClick={() => handleSendInvoice(invoice.id)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                              >
                                <Send size={14} />
                                Send Invoice
                              </button>
                            )}
                            {['sent', 'viewed', 'overdue'].includes(invoice.status) && (
                              <button
                                onClick={() => handleMarkPaid(invoice.id)}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                              >
                                <CheckCircle size={14} />
                                Mark as Paid
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        drivers={drivers}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          loadData();
          addToast('Invoice created successfully', 'success');
        }}
      />

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <InvoiceDetailModal
          isOpen={!!selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          invoice={selectedInvoice}
          driver={drivers.find(d => d.id === selectedInvoice.driver_id)}
          onUpdate={loadData}
        />
      )}

      {/* Payment Method Dialog */}
      <PaymentMethodDialog
        isOpen={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setInvoiceToSend(null);
        }}
        onSelect={handlePaymentMethodSelected}
        hasDefaultPaymentMethod={false}
        canUseStripeInvoice={stripeInvoiceCapability?.canCreate ?? true}
        stripeInvoiceBlockedReason={stripeInvoiceCapability?.reason}
      />

      {/* Click outside to close action menu */}
      {actionMenuOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActionMenuOpen(null)}
        />
      )}
    </div>
  );
};

export default DriverInvoices;
